import { useCallback, useEffect, useMemo, useState } from "react"
import { getSelectedOrganizationId } from "@/utils"
import {
  buildRememberedTableStateStorageKey,
  sanitizeRememberedQueryParams,
} from "@/utils/tableState"
import type {
  UseRememberedTableQueryParamsOptions,
  UseRememberedTableQueryParamsResult,
} from "./useRememberedTableQueryParams.types"

const getInitialQueryParams = (
  storageKey: string,
  defaultQueryParams: UseRememberedTableQueryParamsOptions["defaultQueryParams"]
) => {
  try {
    const rawValue = localStorage.getItem(storageKey)
    if (!rawValue) {
      return defaultQueryParams
    }

    const parsedValue = JSON.parse(rawValue) as unknown
    return sanitizeRememberedQueryParams(parsedValue, defaultQueryParams)
  } catch {
    localStorage.removeItem(storageKey)
    return defaultQueryParams
  }
}

export const useRememberedTableQueryParams = ({
  tableId,
  defaultQueryParams,
  scope,
}: UseRememberedTableQueryParamsOptions): UseRememberedTableQueryParamsResult => {
  const defaultsSignature = JSON.stringify(defaultQueryParams)
  const stableDefaultQueryParams = useMemo(
    () => defaultQueryParams,
    [defaultsSignature]
  )

  const organizationId = getSelectedOrganizationId()

  const storageKey = useMemo(
    () =>
      buildRememberedTableStateStorageKey({
        tableId,
        organizationId,
        scope,
      }),
    [organizationId, scope, tableId]
  )

  const [queryParams, setQueryParams] = useState(() =>
    getInitialQueryParams(storageKey, stableDefaultQueryParams)
  )

  useEffect(() => {
    setQueryParams(getInitialQueryParams(storageKey, stableDefaultQueryParams))
  }, [stableDefaultQueryParams, storageKey])

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(queryParams))
  }, [queryParams, storageKey])

  const resetQueryParams = useCallback(() => {
    localStorage.removeItem(storageKey)
    setQueryParams(stableDefaultQueryParams)
  }, [stableDefaultQueryParams, storageKey])

  return {
    queryParams,
    setQueryParams,
    resetQueryParams,
    storageKey,
  }
}
